import '../css/app.css';
import './bootstrap';

import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createApp, h, DefineComponent } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import { createPinia } from 'pinia';
import { i18nVue } from 'laravel-vue-i18n';
import ChatRoot from '@/components/ChatRoot.vue' // ny komponent för chatten
import {
    capturePostHogPageview,
    initPostHog,
    syncPostHogUser,
    type PostHogConfig,
} from '@/lib/posthog'

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';
const pinia = createPinia()

type TenantBrandProps = {
    tenant?: { id?: string }
    tenant_brand_colors?: { primary_hsl?: string; accent_hsl?: string }
    analytics?: { posthog?: PostHogConfig | null }
    auth?: { user?: { id: number } | null }
    tenant_info?: { tenant_name?: string | null }
}

type AxiosWindow = typeof window & {
    __TENANT_ID?: string
    axios?: { defaults: { headers: { common: Record<string, string> } } }
}

function syncPostHogFromPage(props?: TenantBrandProps) {
    const config = props?.analytics?.posthog
    initPostHog(config ?? null)
    syncPostHogUser(props)
    if (props && window.location.href) {
        capturePostHogPageview(window.location.href)
    }
}

/** Apply tenant brand colors to :root so UI updates after saving (Inertia does not re-render head). Also sets --chart-1 so dashboard/diagrams use primary. */
function applyTenantBrandColors(props?: TenantBrandProps) {
    const colors = props?.tenant_brand_colors
    if (!colors?.primary_hsl && !colors?.accent_hsl) return
    const root = document.documentElement
    if (colors.primary_hsl) {
        root.style.setProperty('--color-primary', colors.primary_hsl)
        root.style.setProperty('--chart-1', colors.primary_hsl)
    }
    if (colors.accent_hsl) root.style.setProperty('--color-accent', colors.accent_hsl)
}

function syncTenantHeader(props?: TenantBrandProps) {
    const w = window as AxiosWindow
    const tenantId = props?.tenant?.id ?? w.__TENANT_ID

    if (tenantId && w.axios) {
        w.axios.defaults.headers.common['X-Tenant'] = tenantId
    }
}

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(
            `./pages/${name}.vue`,
            import.meta.glob<DefineComponent>('./pages/**/*.vue')
        ),
    setup({ el, App, props, plugin }) {
        const initialProps = ((props as { initialPage?: { props?: TenantBrandProps }; page?: { props?: TenantBrandProps } } | undefined)?.initialPage?.props
            ?? (props as { page?: { props?: TenantBrandProps } } | undefined)?.page?.props)

        applyTenantBrandColors(initialProps)
        syncTenantHeader(initialProps)
        syncPostHogFromPage(initialProps)
        /** Inertia 3: Router has no `page`; `finish` has no `page` in detail — use `success` (detail.page). */
        router.on('success', (event) => {
            const p = (event as CustomEvent<{ page?: { props?: TenantBrandProps; url?: string } }>)?.detail?.page
            if (p?.props) {
                applyTenantBrandColors(p.props)
                syncTenantHeader(p.props)
                syncPostHogUser(p.props)
            }
            if (p?.url) {
                capturePostHogPageview(p.url)
            }
        })
        return createApp({ render: () => h(App, props ?? {}) })
            .use(plugin)
            .use(i18nVue, {
                resolve: async (lang) => {
                    const langs = import.meta.glob('../../lang/*.json');
                    return await langs[`../../lang/${lang}.json`]();
                },
            })
            .use(pinia)
            .use(ZiggyVue)
            .mount(el);
    },
    progress: {
        delay: 80,
        color: '#104547',
        showSpinner: false,
        includeCSS: true,
    },
});

// Add impersonate class if needed
if (document.getElementById('impersonate-banner')) {
    document.body.classList.add('has-impersonate-banner');
}

const chatRoot = document.getElementById('chat-root')
if (chatRoot) {
    createApp(ChatRoot)
        .use(pinia) // dela store om du vill
        .mount(chatRoot)
}
